// components/problems/types.ts
import Fuse from 'fuse.js'
import type { ProblemTopic } from '@/lib/problems/problem-topics'
import { canonicalStatus } from '@/lib/progress-status'

// --- Shared types ---

export interface BrowseProblem {
  id: string
  slug: string
  title: string
  externalId: string | null
  difficulty: { name: string; color: string | null } | null
  topics: ProblemTopic[]
}

export type FilterOperator = 'is' | 'is not'

export interface FilterValues {
  status: { operator: FilterOperator; values: string[] }
  difficulty: { operator: FilterOperator; values: string[] }
  topics: { operator: FilterOperator; values: string[] }
  lists: { operator: FilterOperator; values: string[] }
}

export const defaultFilters: FilterValues = {
  status: { operator: 'is', values: [] },
  difficulty: { operator: 'is', values: [] },
  topics: { operator: 'is', values: [] },
  lists: { operator: 'is', values: [] },
}

export type SortOption = 'default' | 'difficulty' | 'questionId' | 'status'

// --- Fuse.js search helper ---

export function createProblemsFuse(problems: BrowseProblem[]) {
  return new Fuse(problems, {
    keys: [
      { name: 'title', weight: 0.7 },
      { name: 'externalId', weight: 0.3 },
    ],
    threshold: 0.4,
    minMatchCharLength: 2,
  })
}

export function searchProblems(
  problems: BrowseProblem[],
  fuse: Fuse<BrowseProblem>,
  query: string,
): BrowseProblem[] {
  const trimmed = query.trim()
  if (!trimmed) return problems
  if (/^\d+$/.test(trimmed)) {
    return problems.filter((p) => p.externalId === trimmed)
  }
  return fuse.search(trimmed).map((r) => r.item)
}

// --- Filter logic ---

export function applyFilters(
  problems: BrowseProblem[],
  filters: FilterValues,
  matchMode: 'any' | 'all',
  userProgress?: Record<string, string>,
  memberships?: Record<string, string[]>,
): BrowseProblem[] {
  const hasActiveFilters =
    filters.status.values.length > 0 ||
    filters.difficulty.values.length > 0 ||
    filters.topics.values.length > 0 ||
    (filters.lists?.values.length ?? 0) > 0

  if (!hasActiveFilters) return problems

  return problems.filter((problem) => {
    const checks: boolean[] = []

    if (filters.difficulty.values.length > 0) {
      const match = filters.difficulty.values.some(
        (d) => problem.difficulty?.name === d,
      )
      checks.push(filters.difficulty.operator === 'is' ? match : !match)
    }
    if (filters.topics.values.length > 0) {
      const match = filters.topics.values.some((t) =>
        problem.topics.some((topic) => topic.name === t),
      )
      checks.push(filters.topics.operator === 'is' ? match : !match)
    }
    if (filters.status.values.length > 0) {
      // Duplicate-propagated statuses filter with their direct counterpart.
      const resolved =
        canonicalStatus(userProgress?.[problem.id]) ?? 'not started'
      const match = filters.status.values.includes(resolved)
      checks.push(filters.status.operator === 'is' ? match : !match)
    }
    if ((filters.lists?.values.length ?? 0) > 0) {
      const problemLists = memberships?.[problem.id] ?? []
      const match = filters.lists.values.some((listId) =>
        listId === '__all__'
          ? problemLists.length > 0
          : problemLists.includes(listId),
      )
      checks.push(filters.lists.operator === 'is' ? match : !match)
    }

    return matchMode === 'any' ? checks.some(Boolean) : checks.every(Boolean)
  })
}

// --- Sort logic ---

export function sortProblems(
  problems: BrowseProblem[],
  sortBy: SortOption,
  sortDir: 'asc' | 'desc',
  userProgress?: Record<string, string>,
): BrowseProblem[] {
  if (sortBy === 'default') return problems

  const dir = sortDir === 'asc' ? 1 : -1

  return [...problems].sort((a, b) => {
    if (sortBy === 'questionId') {
      const aId = a.externalId ? Number(a.externalId) : Infinity
      const bId = b.externalId ? Number(b.externalId) : Infinity
      return (aId - bId) * dir
    }
    if (sortBy === 'difficulty') {
      const order: Record<string, number> = { Easy: 0, Medium: 1, Hard: 2 }
      const aVal = a.difficulty ? (order[a.difficulty.name] ?? 3) : 3
      const bVal = b.difficulty ? (order[b.difficulty.name] ?? 3) : 3
      return (aVal - bVal) * dir
    }
    if (sortBy === 'status') {
      const statusOrder: Record<string, number> = {
        solved: 0,
        attempted: 1,
      }
      const aStatus = canonicalStatus(userProgress?.[a.id])
      const bStatus = canonicalStatus(userProgress?.[b.id])
      const aVal = aStatus ? (statusOrder[aStatus] ?? 2) : 2
      const bVal = bStatus ? (statusOrder[bStatus] ?? 2) : 2
      return (aVal - bVal) * dir
    }
    return 0
  })
}

// --- Helper: count active filters ---

export function countActiveFilters(filters: FilterValues): number {
  return (
    (filters.status.values.length > 0 ? 1 : 0) +
    (filters.difficulty.values.length > 0 ? 1 : 0) +
    (filters.topics.values.length > 0 ? 1 : 0) +
    ((filters.lists?.values.length ?? 0) > 0 ? 1 : 0)
  )
}

// --- Helper: derive available topics from problem data ---

export function deriveAvailableTopics(problems: BrowseProblem[]): string[] {
  const names = new Set<string>()
  for (const p of problems) {
    for (const t of p.topics) names.add(t.name)
  }
  return [...names].sort()
}

export function deriveAvailableDifficulties(
  problems: BrowseProblem[],
): string[] {
  const diffs = new Set<string>()
  for (const p of problems) {
    if (p.difficulty) diffs.add(p.difficulty.name)
  }
  const order = ['Easy', 'Medium', 'Hard']
  return order.filter((d) => diffs.has(d))
}
